import { IconQuestion } from '@consta/icons/IconQuestion'
import { Text } from '@consta/uikit/Text'
import type { TextFieldPropOnChange } from '@consta/uikit/TextField'
import { TextField } from '@consta/uikit/TextField'
import type { SegySampleParamsInfo } from '@core/core/types'
import { isDefined } from 'common/helpers'
import type { Nullable } from 'common/types'
import type { FC } from 'react'
import { useEffect, useState } from 'react'

import { MAX_START_RECORD_VALUE } from '../../constants'
import css from './Setup.css'
import type { OptionsFormProps } from './types'

export const OptionsForm: FC<OptionsFormProps> = ({
  setSamplesValue,
  samplesProps,
  samplesValue,
}) => {
  const [minValue, setMinValue] = useState<number>()
  const { label, name } = samplesProps
  const isScaleField = name === 'scale'
  const isStartRecordField = name === 'startRecord'
  const isIntervalField = name === 'interval'
  const value = samplesValue?.[name]?.toString() || null
  const [optionFormStatus, setOptionFormStatus] = useState<'alert' | undefined>(undefined)
  const [optionFormValue, setOptionFormValue] = useState<Nullable<string>>(value)

  useEffect(() => {
    setOptionFormValue(value)
  }, [value])

  useEffect(() => {
    setOptionFormStatus(getOptionFormStatus(optionFormValue))
    if (isDefined(optionFormValue)) {
      setNewSamplesValue(optionFormValue)
    }
  }, [optionFormValue])

  useEffect(() => {
    if (isIntervalField || isScaleField) {
      setMinValue(0)
    } else if (isStartRecordField) {
      setMinValue(-MAX_START_RECORD_VALUE)
    }
  }, [name])

  const handleValueChange: TextFieldPropOnChange = value => {
    if (isStartRecordField && isDefined(value)) {
      handleRecordChange(value)
      return
    }
    setOptionFormValue(value)
  }

  function handleRecordChange(value: string) {
    let newValue = +value
    if (Math.abs(newValue) > MAX_START_RECORD_VALUE) {
      newValue = MAX_START_RECORD_VALUE * Math.sign(newValue)
    }
    setOptionFormValue(Math.floor(newValue).toString())
  }

  function getOptionFormStatus(newSamplesValue: Nullable<string>): 'alert' | undefined {
    if (isStartRecordField) {
      return isDefined(newSamplesValue) ? undefined : 'alert'
    }
    return isDefined(newSamplesValue) && +newSamplesValue > 0 ? undefined : 'alert'
  }

  function setNewSamplesValue(newSamplesValue: string): void {
    const newValue: SegySampleParamsInfo = {
      ...samplesValue,
      [name]: isDefined(newSamplesValue) ? +newSamplesValue : 0,
    }
    setSamplesValue(newValue)
  }

  return (
    <div className={css.optionsContainer}>
      <IconQuestion size="s" view="secondary" className={css.optionIcon} />
      <Text size="s" view="secondary" className={css.optionsLabel}>
        {label}
      </Text>
      <TextField
        size="s"
        className={css.optionsFormField}
        type="number"
        max={isStartRecordField ? MAX_START_RECORD_VALUE : undefined}
        name={name}
        value={optionFormValue}
        status={optionFormStatus}
        data-testid={name}
        min={minValue}
        onChange={handleValueChange}
      />
    </div>
  )
}
